/**
 * Accessibility Utilities for Hermes UI
 *
 * Provides helpers for keyboard navigation, focus management, and ARIA support
 * Following WCAG 2.1 AA standards
 */

/**
 * Trap focus within a container element (for modals/dialogs)
 */
export const trapFocus = (element: HTMLElement | null) => {
  if (!element) return () => {};

  const focusableElements = element.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusable) {
        lastFocusable?.focus();
        e.preventDefault();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusable) {
        firstFocusable?.focus();
        e.preventDefault();
      }
    }
  };

  element.addEventListener('keydown', handleTabKey);

  return () => {
    element.removeEventListener('keydown', handleTabKey);
  };
};

/**
 * Store and restore focus for modal dialogs
 */
export class FocusManager {
  private previousFocus: HTMLElement | null = null;

  /**
   * Save current focus and focus the given element
   */
  capture(elementToFocus?: HTMLElement | null) {
    this.previousFocus = document.activeElement as HTMLElement;
    if (elementToFocus) {
      // Delay focus to next frame to ensure element is rendered
      requestAnimationFrame(() => {
        elementToFocus.focus();
      });
    }
  }

  /**
   * Restore previously saved focus
   */
  restore() {
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      // Delay to allow modal close animation
      requestAnimationFrame(() => {
        this.previousFocus?.focus();
      });
    }
    this.previousFocus = null;
  }
}

/**
 * Generate unique IDs for aria-describedby relationships
 */
let idCounter = 0;
export const generateAriaId = (prefix: string = 'hermes-aria'): string => {
  idCounter++;
  return `${prefix}-${idCounter}`;
};

/**
 * Announce message to screen readers via live region
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
) => {
  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', priority);
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.className = 'sr-only';
  liveRegion.style.cssText = `
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `;

  document.body.appendChild(liveRegion);

  // Delay announcement to ensure screen reader picks it up
  setTimeout(() => {
    liveRegion.textContent = message;
  }, 100);

  // Clean up after announcement
  setTimeout(() => {
    document.body.removeChild(liveRegion);
  }, 3000);
};

/**
 * Keyboard event handlers
 */
export const keyboardHandlers = {
  onEnter: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      callback();
    }
  },
  onSpace: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      callback();
    }
  },
  onEscape: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      callback();
    }
  },
  onEnterOrSpace: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      callback();
    }
  },
};

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Get focus ring styles for custom interactive elements
 */
export const getFocusRingStyles = (theme: any, color?: string) => ({
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: `2px solid ${color || theme.palette.primary.main}`,
    outlineOffset: 2,
    boxShadow: `0 0 0 4px ${color || theme.palette.primary.main}20`,
  },
});
